import { useEffect } from "react";
import { uuid } from "@/utils/string.ts";
import { DaysShort } from "@/utils/agenda.ts";
import { enqueueSnackbar } from "notistack";
import { Trans, useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useUpdateAvailabilityMutation } from "@/data/availabilty.ts";
import { AvailabilitySlot, UserAvailability } from "@/types";
import {
  DaysEng,
  getTime,
  excludeDate,
  REMINDER_STORAGE_KEY,
} from "@/utils/availability.ts";
import Button from "@edusogno/primitives/button";
import StopIcon from "@/components/icons/StopIcon.tsx";
import CheckIcon from "@/components/icons/availability/CheckIcon.tsx";
import CrossIcon from "@/components/icons/availability/CrossIcon.tsx";
import useGlobals from "@/utils/hooks/useGlobals.ts";
import LoadingButton from "@edusogno/primitives/loading-button";
import DashboardContentLayout from "@/layouts/DashboardContentLayout.tsx";

const times = getTime();

interface ConfirmState {
  slots: AvailabilitySlot[];
  availabilities: UserAvailability;
}

export default function ConfirmAvailability() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigation = useNavigate();
  const { mutate, isLoading } = useUpdateAvailabilityMutation();

  const { slots, availabilities } = location?.state as ConfirmState;

  const isSlotSelected = (id: string) => {
    const item = slots.find((r) => r.id === id);
    return item ? item.isSelected : false;
  };

  const handleConfirm = async () => {
    const payload = slots.map((a) => ({
      status: Number(!a.isSelected),
      id: `disp ${a.value.time} ${DaysEng.indexOf(a.value.day)}`,
    }));

    mutate(
      {
        data_range: availabilities?.date.join("/"),
        array: JSON.stringify([{ Stringa: payload }]),
      },
      {
        onSuccess: (response) => {
          if (response) {
            enqueueSnackbar(t("availability.update_success"), {
              variant: "success",
            });
            confirm();
            localStorage.removeItem(REMINDER_STORAGE_KEY);
          }
        },
      },
    );
  };

  const { setGlobalState } = useGlobals();

  useEffect(() => {
    setGlobalState("backgroundClass", "bg-light");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <DashboardContentLayout>
      <div className="h-full overflow-y-auto flex flex-col justify-between">
        <header className="flex flex-col items-start mb-2">
          <h3 className="text-2xl text-brand-secondary font-semibold">
            {t("availability.recap")}
          </h3>

          <p className="text-sm font-normal text-start">
            <Trans
              i18nKey="availability.available"
              components={{
                b_anchor: <b className="text-brand-dark-green" />,
              }}
            />
          </p>
        </header>

        <div className="flex flex-row w-full h-full">
          <div className="grid grid-cols-1 gap-y-4 mr-2 mt-6">
            {times.map((i) => (
              <p
                key={uuid()}
                className="text-brand text-[11px] uppercase text-center"
              >
                {i}
              </p>
            ))}
          </div>

          <div className="flex flex-col w-full">
            <div className="grid grid-cols-7 mb-2  gap-0.5">
              {DaysShort.map((i, key) => (
                <p
                  key={`${i}-key-${key}`}
                  className="text-brand text-center text-xs uppercase"
                >
                  {i}
                </p>
              ))}
            </div>

            <div
              id="slotGrid"
              className="grid grid-cols-7 auto-rows-fr w-full h-full gap-1"
            >
              {slots.map((i) => (
                <div className="m-auto" key={i.id}>
                  {excludeDate(i.value) ? (
                    <>
                      <StopIcon className="stroke-shade-ten w-3 h-auto" />
                    </>
                  ) : !isSlotSelected(i.id) ? (
                    <CheckIcon className="stroke-brand-dark-green w-3 h-auto" />
                  ) : (
                    <CrossIcon className="stroke-shade-three w-3 h-auto" />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="flex h-full flex-row w-full justify-start items-center gap-x-2 mt-4">
          <Button
            size="md"
            color="primary"
            variant="contained"
            onClick={() => navigation(-1)}
            disabled={isLoading}
          >
            {t("btns.edit")}
          </Button>

          <LoadingButton
            size="md"
            color="white"
            variant="contained"
            loading={isLoading}
            onClick={() => void handleConfirm()}
            className="[--bg:--green100]"
          >
            {t("btns.confirm")}
          </LoadingButton>
        </div>
      </div>
    </DashboardContentLayout>
  );
}
